class RugCatalogViewer extends HTMLElement {
  constructor() {
    super();
    this.manifestUrl = this.dataset.manifestUrl;

    this.loadingEl = this.querySelector('[data-rug-catalog-loading]');
    this.appEl = this.querySelector('[data-rug-catalog-app]');
    this.contentEl = this.querySelector('[data-rug-catalog-content]');
    this.catnavEl = this.querySelector('[data-rug-catalog-catnav]');
    this.activeCatEl = this.querySelector('[data-rug-catalog-active-cat]');
    this.threadFillEl = this.querySelector('[data-rug-catalog-thread]');

    this.lightboxEl = this.querySelector('[data-rug-catalog-lightbox]');
    this.lbImgEl = this.querySelector('[data-rug-catalog-lb-img]');
    this.lbCatEl = this.querySelector('[data-rug-catalog-lb-cat]');
    this.lbDimsEl = this.querySelector('[data-rug-catalog-lb-dims]');
    this.lbCounterEl = this.querySelector('[data-rug-catalog-lb-counter]');
    this.lbPrevEl = this.querySelector('[data-rug-catalog-lb-prev]');
    this.lbNextEl = this.querySelector('[data-rug-catalog-lb-next]');
    this.lbCloseEl = this.querySelector('[data-rug-catalog-lb-close]');

    this.categories = [];
    this.all = [];
    this.lbIndex = 0;
    this.resizeTimer = null;
    this.buildObserver = null;
    this.imageObserver = null;
    this.imageQueue = [];
    this.activeImageLoads = 0;
    this.maxConcurrentImageLoads = 6;

    this.init();
  }

  async init() {
    try {
      const response = await fetch(this.manifestUrl);
      const manifest = await response.json();
      this.prepareCategories(manifest);
      this.setupImageQueue();
      this.buildSkeleton();
      this.loadingEl.hidden = true;
      this.appEl.hidden = false;
      this.bindEvents();
      this.setupLazyBuild();
    } catch (error) {
      this.loadingEl.textContent = 'Could not load the catalog. Please refresh the page.';
    }
  }

  slugify(value) {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  parseMeta(filename) {
    const decoded = decodeURIComponent(filename);
    const match = decoded.match(/--\s*([\d.]+)x([\d.]+)\s*=\s*([\d.]+)M2\s*([\d.]+x[\d.]+)\s*ft/i);
    if (!match) return { cm: '', m2: '', ft: '', ratio: 1.3 };
    const w = parseFloat(match[1]);
    const h = parseFloat(match[2]);
    return {
      cm: `${match[1]}×${match[2]} cm`,
      m2: `${match[3]} m²`,
      ft: `${match[4]} ft`,
      ratio: w && h ? w / h : 1.3,
    };
  }

  /* metadata pass only - no DOM/img creation, so this stays cheap even at 2000+ images */
  prepareCategories(manifest) {
    const categoryNames = Object.keys(manifest).sort((a, b) => a.localeCompare(b));

    categoryNames.forEach((categoryName) => {
      const images = manifest[categoryName].map((image) => {
        const meta = this.parseMeta(image.name);
        const globalIndex = this.all.length;
        this.all.push({ category: categoryName, url: image.url, meta });
        return { ...image, meta, globalIndex };
      });

      this.categories.push({
        name: categoryName,
        slug: this.slugify(categoryName),
        images,
        built: false,
      });
    });
  }

  /* cheap DOM: pill nav + empty section shells, no images yet */
  buildSkeleton() {
    this.categories.forEach((category) => {
      const pill = document.createElement('a');
      pill.className = 'rug-catalog__pill';
      pill.href = '#' + category.slug;
      pill.dataset.slug = category.slug;
      pill.innerHTML = `${category.name}<span class="rug-catalog__pill-count">${category.images.length}</span>`;
      pill.addEventListener('click', () => this.buildCategory(category));
      this.catnavEl.appendChild(pill);

      const section = document.createElement('section');
      section.className = 'rug-catalog__category';
      section.id = category.slug;
      section.dataset.slug = category.slug;
      section.innerHTML = `
        <div class="rug-catalog__cat-heading">
          <h2 class="rug-catalog__cat-title">${category.name}</h2>
          <span class="rug-catalog__cat-meta">${category.images.length} images</span>
        </div>
        <hr class="rug-catalog__rule">
        <div class="rug-catalog__grid"></div>
      `;
      category.sectionEl = section;
      category.gridEl = section.querySelector('.rug-catalog__grid');
      this.contentEl.appendChild(section);
    });
  }

  /* real DOM: builds actual <img> cards for one category, only called once per category.
     Images get a data-src, not a src - imageObserver below requests each one individually
     as it nears viewport, so a 500-image category doesn't fire 500 concurrent Dropbox
     requests at once (Dropbox throttles/drops requests under that kind of burst). */
  buildCategory(category) {
    if (category.built) return;
    category.built = true;

    const fragment = document.createDocumentFragment();
    category.images.forEach((image) => {
      const figure = document.createElement('figure');
      figure.className = 'rug-catalog__card';
      figure.dataset.ratio = image.ratio || image.meta.ratio;
      figure.innerHTML = `
        <div class="rug-catalog__thumb">
          <img data-src="${image.url}" decoding="async" alt="${category.name} ${image.meta.cm}">
        </div>
        <figcaption class="rug-catalog__tag">${image.meta.cm} &middot; ${image.meta.m2}<span class="rug-catalog__tag-sub">${image.meta.ft}</span></figcaption>
      `;
      figure.addEventListener('click', () => this.openLightbox(image.globalIndex));
      fragment.appendChild(figure);
    });

    category.gridEl.appendChild(fragment);
    category.gridEl.querySelectorAll('img[data-src]').forEach((img) => this.imageObserver.observe(img));
    this.layoutMasonry(category.gridEl);
  }

  setupImageQueue() {
    this.imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          this.imageObserver.unobserve(img);
          this.queueImageLoad(img);
        });
      },
      { rootMargin: '400px 0px 400px 0px' }
    );
  }

  /* caps how many Dropbox requests are in flight at once across the whole page */
  queueImageLoad(img) {
    this.imageQueue.push(img);
    this.drainImageQueue();
  }

  drainImageQueue() {
    while (this.activeImageLoads < this.maxConcurrentImageLoads && this.imageQueue.length) {
      const img = this.imageQueue.shift();
      const src = img.dataset.src;
      delete img.dataset.src;
      this.activeImageLoads++;
      const done = () => {
        this.activeImageLoads--;
        this.drainImageQueue();
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      img.src = src;
    }
  }

  setupLazyBuild() {
    this.buildObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const category = this.categories.find((c) => c.slug === entry.target.dataset.slug);
          if (category) this.buildCategory(category);
          this.buildObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '250px 0px 250px 0px' }
    );
    this.categories.forEach((category) => this.buildObserver.observe(category.sectionEl));
  }

  colCountFor(width) {
    if (width <= 780) return 2;
    if (width <= 1100) return 3;
    if (width <= 1500) return 4;
    if (width <= 1900) return 5;
    return 6;
  }

  layoutMasonry(grid) {
    const cards = [...grid.children];
    if (!cards.length) {
      grid.style.height = '0px';
      return;
    }
    const winWidth = window.innerWidth;
    const cols = this.colCountFor(winWidth);
    const gap = winWidth <= 780 ? 10 : 20;
    const thumbPad = (winWidth <= 780 ? 6 : 10) * 2;
    const containerWidth = grid.clientWidth;
    const colWidth = (containerWidth - gap * (cols - 1)) / cols;
    const colHeights = new Array(cols).fill(0);

    cards.forEach((card) => {
      const ratio = parseFloat(card.dataset.ratio) || 1.3;
      const imgHeight = colWidth / ratio;
      const captionHeight = card.querySelector('.rug-catalog__tag') ? 40 : 0;
      const cardHeight = imgHeight + thumbPad + captionHeight;
      let col = 0;
      for (let i = 1; i < cols; i++) {
        if (colHeights[i] < colHeights[col]) col = i;
      }
      card.style.width = colWidth + 'px';
      card.style.left = col * (colWidth + gap) + 'px';
      card.style.top = colHeights[col] + 'px';
      colHeights[col] += cardHeight + gap;
    });

    grid.style.height = Math.max(...colHeights) - gap + 'px';
  }

  layoutAllMasonry() {
    this.categories.forEach((category) => {
      if (category.built) this.layoutMasonry(category.gridEl);
    });
  }

  bindEvents() {
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.layoutAllMasonry(), 150);
    });

    const sections = this.categories.map((c) => c.sectionEl);
    const pills = [...this.querySelectorAll('.rug-catalog__pill')];
    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const slug = entry.target.dataset.slug;
          pills.forEach((p) => p.classList.toggle('is-active', p.dataset.slug === slug));
          const pill = pills.find((p) => p.dataset.slug === slug);
          if (pill) {
            this.activeCatEl.textContent = entry.target.querySelector('.rug-catalog__cat-title').textContent;
            pill.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
          }
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    sections.forEach((section) => spy.observe(section));

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const doc = document.documentElement;
        const pct = (doc.scrollTop / (doc.scrollHeight - doc.clientHeight)) * 100;
        this.threadFillEl.style.width = pct + '%';
        ticking = false;
      });
    });

    this.lbPrevEl.addEventListener('click', () => this.lbGo(-1));
    this.lbNextEl.addEventListener('click', () => this.lbGo(1));
    this.lbCloseEl.addEventListener('click', () => this.closeLightbox());
    this.lightboxEl.addEventListener('click', (event) => {
      if (event.target === this.lightboxEl) this.closeLightbox();
    });

    document.addEventListener('keydown', (event) => {
      if (!this.lightboxEl.classList.contains('is-open')) return;
      if (event.key === 'ArrowRight') this.lbGo(1);
      if (event.key === 'ArrowLeft') this.lbGo(-1);
      if (event.key === 'Escape') this.closeLightbox();
    });

    let touchStartX = 0;
    this.lightboxEl.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0].clientX;
    });
    this.lightboxEl.addEventListener('touchend', (event) => {
      const dx = event.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 40) this.lbGo(dx > 0 ? -1 : 1);
    });
  }

  openLightbox(index) {
    this.lbIndex = index;
    this.renderLightbox();
    this.lightboxEl.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeLightbox() {
    this.lightboxEl.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  lbGo(delta) {
    const nextIndex = this.lbIndex + delta;
    if (nextIndex < 0 || nextIndex > this.all.length - 1) return;
    this.lbIndex = nextIndex;
    this.renderLightbox();
  }

  renderLightbox() {
    const item = this.all[this.lbIndex];
    this.lbImgEl.src = item.url;
    this.lbImgEl.alt = item.category;
    this.lbCatEl.textContent = item.category;
    this.lbDimsEl.textContent = [item.meta.cm, item.meta.m2, item.meta.ft].filter(Boolean).join(' · ');
    this.lbCounterEl.textContent = `${this.lbIndex + 1} / ${this.all.length}`;
    this.lbPrevEl.disabled = this.lbIndex === 0;
    this.lbNextEl.disabled = this.lbIndex === this.all.length - 1;
  }
}

customElements.define('rug-catalog-viewer', RugCatalogViewer);
